import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, X } from "lucide-react";
import { notify } from "@/components/ui/dialogs";
import { get, post, put, can } from "@/utils/service";
import { apiMessage } from "@/utils/api";
import { Panel, input, primary, secondary } from "./shared";
import AppSelect from "@/components/ui/app-select";

interface Stage { id?: string; name: string; probability: number; category: "open" | "won" | "lost"; color: string | null; is_active: boolean; deals_count?: number }
interface Pipeline { id: string; name: string; is_default: boolean; is_active: boolean; deals_count: number; stages: Stage[] }

/** Sales pipelines and their stages: order, win probability, and which stages mean won or lost. */
export default function Pipelines() {
  const editable = can("settings.manage");
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [current, setCurrent] = useState<string>("");
  const [stages, setStages] = useState<Stage[]>([]);
  const [busy, setBusy] = useState(false);

  const load = async (keep?: string) => {
    const r = await get<{ data: { pipelines: Pipeline[] } }>("/pipelines");
    setPipelines(r.data.pipelines);
    const p = r.data.pipelines.find((x) => x.id === keep) ?? r.data.pipelines.find((x) => x.is_default) ?? r.data.pipelines[0];
    if (p) { setCurrent(p.id); setStages([...p.stages].sort((a: any, b: any) => a.position - b.position)); }
  };
  useEffect(() => { load().catch((e) => notify(apiMessage(e, "Could not load pipelines."))); }, []);

  const pick = (id: string) => { const p = pipelines.find((x) => x.id === id); if (p) { setCurrent(id); setStages([...p.stages].sort((a: any, b: any) => a.position - b.position)); } };
  const update = (i: number, patch: Partial<Stage>) => setStages((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i: number, d: number) => setStages((s) => { const n = [...s]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); return n; });

  const saveStages = async () => {
    setBusy(true);
    try {
      await put(`/pipelines/${current}/stages`, { stages: stages.map((s) => ({ id: s.id, name: s.name, probability: Number(s.probability), category: s.category, color: s.color || null, is_active: s.is_active })) });
      notify("Pipeline saved");
      load(current);
    } catch (e) { notify(apiMessage(e, "Could not save the stages.")); }
    finally { setBusy(false); }
  };
  const addPipeline = async () => {
    const name = window.prompt("Name of the new pipeline");
    if (!name) return;
    try { const r = await post<{ data: { pipeline: Pipeline } }>("/pipelines", { name }); notify("Pipeline created with standard stages"); load(r.data.pipeline.id); }
    catch (e) { notify(apiMessage(e, "Could not create the pipeline.")); }
  };
  const makeDefault = async () => {
    try { await put(`/pipelines/${current}`, { is_default: true }); notify("Default pipeline changed"); load(current); }
    catch (e) { notify(apiMessage(e, "Could not change the default.")); }
  };

  const p = pipelines.find((x) => x.id === current);

  return (
    <Panel title="Pipelines" description="The stages deals move through" actions={editable && <button onClick={addPipeline} className={secondary}>New pipeline</button>}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <AppSelect className={`${input} w-auto`} value={current} onChange={(e) => pick(e.target.value)}>
          {pipelines.map((x) => <option key={x.id} value={x.id}>{x.name}{x.is_default ? " (default)" : ""}</option>)}
        </AppSelect>
        {p && <span className="text-xs text-gray-500">{p.deals_count} open deal{p.deals_count === 1 ? "" : "s"}</span>}
        {p && !p.is_default && editable && <button onClick={makeDefault} className={secondary}>Make default</button>}
      </div>
      <div className="space-y-2">
        {stages.map((s, i) => (
          <div key={s.id ?? `new-${i}`} className={`grid grid-cols-[auto_1fr_6rem_7rem_auto_auto] items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 ${s.is_active ? "" : "opacity-50"}`}>
            <div className="flex flex-col">
              <button aria-label="Move up" disabled={!editable || i === 0} onClick={() => move(i, -1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
              <button aria-label="Move down" disabled={!editable || i === stages.length - 1} onClick={() => move(i, 1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
            </div>
            <input className={input} disabled={!editable} value={s.name} onChange={(e) => update(i, { name: e.target.value })} />
            <div className="flex items-center gap-1"><input type="number" min="0" max="100" className={input} disabled={!editable} value={s.probability} onChange={(e) => update(i, { probability: Number(e.target.value) })} /><span className="text-xs text-gray-500">%</span></div>
            <AppSelect className={input} disabled={!editable} value={s.category} onChange={(e) => update(i, { category: e.target.value as Stage["category"] })}><option value="open">In progress</option><option value="won">Won</option><option value="lost">Lost</option></AppSelect>
            <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" className="accent-primary" disabled={!editable} checked={s.is_active} onChange={(e) => update(i, { is_active: e.target.checked })} />Active</label>
            {editable && !s.id ? <button aria-label="Remove stage" onClick={() => setStages((x) => x.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button> : <span className="w-4" />}
          </div>
        ))}
      </div>
      {editable && (
        <div className="mt-4 flex justify-between">
          <button onClick={() => setStages((s) => [...s.filter((x) => x.category === "open"), { name: "New stage", probability: 50, category: "open", color: null, is_active: true }, ...s.filter((x) => x.category !== "open")])} className={secondary}>Add stage</button>
          <button onClick={saveStages} disabled={busy} className={primary}>{busy ? "Saving..." : "Save pipeline"}</button>
        </div>
      )}
      <p className="mt-3 text-[11px] text-gray-500">Stages that hold deals can't be removed; turn them off instead. Moving a deal into a Won or Lost stage closes it.</p>
    </Panel>
  );
}
